<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { ChevronDown, Check, ArrowUpDown } from 'lucide-vue-next'

/**
 * Sort, as a menu rather than a native `<select>`.
 *
 * The native control rendered as an empty box: with `appearance: none` on a dark
 * surface the selected value did not paint, so the only way to find out what the
 * listing was sorted by was to open it. Two rounds of styling did not fix it,
 * and a control whose current state is invisible is not worth defending.
 *
 * This is the same popover as the filter controls beside it, so all five read as
 * one set — and unlike a `<select>` it can show what it is doing: the label is
 * "Sort: Most students", and the chosen option carries a tick.
 */
const props = defineProps({
  modelValue: { type: String, required: true },
  options: { type: Array, required: true },
})

const emit = defineEmits(['update:modelValue', 'change'])

const open = ref(false)
const root = ref(null)

const current = computed(
  () => props.options.find((option) => option.value === props.modelValue) ?? props.options[0]
)

const choose = (value) => {
  open.value = false
  if (value === props.modelValue) return
  emit('update:modelValue', value)
  emit('change')
}

const onDocumentClick = (event) => {
  if (root.value && !root.value.contains(event.target)) open.value = false
}
const onKeydown = (event) => {
  if (event.key === 'Escape') open.value = false
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick)
  document.addEventListener('keydown', onKeydown)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick)
  document.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <div ref="root" class="relative">
    <button
      type="button"
      class="flex h-11 w-full items-center gap-2 rounded-md border border-line bg-surface px-3.5 text-sm font-semibold text-ink transition-colors hover:bg-surface-2 sm:w-auto"
      :aria-expanded="open"
      aria-haspopup="listbox"
      @click="open = !open"
    >
      <ArrowUpDown class="size-4 shrink-0 text-muted" />
      <span class="text-muted">Sort:</span>
      <span class="truncate">{{ current.label }}</span>
      <ChevronDown class="size-4 shrink-0 opacity-60 transition-transform duration-200" :class="{ 'rotate-180': open }" />
    </button>

    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0 -translate-y-1"
      leave-active-class="transition duration-100 ease-in"
      leave-to-class="opacity-0 -translate-y-1"
    >
      <div
        v-if="open"
        class="absolute right-0 top-full z-30 mt-2 w-56 rounded-lg border border-line bg-surface p-2 shadow-lg"
        role="listbox"
        aria-label="Sort tutors"
      >
        <button
          v-for="option in options"
          :key="option.value"
          type="button"
          role="option"
          :aria-selected="option.value === modelValue"
          class="flex w-full items-center justify-between gap-3 rounded-md px-3 py-2.5 text-left text-sm transition-colors hover:bg-surface-2"
          :class="option.value === modelValue ? 'font-semibold text-brand' : 'text-ink'"
          @click="choose(option.value)"
        >
          {{ option.label }}
          <Check v-if="option.value === modelValue" class="size-4 shrink-0" />
        </button>
      </div>
    </Transition>
  </div>
</template>
